import type { ProjectileSpec } from "../models/ProjectileSpec.js";
import type { ModuleSlotType } from "../models/ModuleTypes.js";
import type { ModulePiece } from "../data/modulePieces.js";
import { MODULE_BRICK_BALANCE } from "../data/balance.js";
import {
  ALWAYS_PROC,
  applyPipeCriticalEffect,
  applyModuleEffect,
  moduleEffectChance,
  type ModuleProcKind,
  type ModuleEffectData,
} from "./ModuleEffect.js";

/**
 * 발사체 모듈 (지시문 §10). apply는 원본 spec을 mutate하지 않고 새 객체를 반환한다.
 * 런타임 인스턴스는 level + 유효 등급(effectiveRarity)을 들고 있으며, apply 시 **등급별 effect 데이터** +
 * 공통 레벨 계수를 적용한다. 효과는 코드가 아니라 modules.tsv effect{Grade} 컬럼이 SSoT.
 */
export interface ProjectileModule {
  readonly id: string;
  readonly displayName: string;
  readonly description: string;
  readonly slotType: ModuleSlotType;
  readonly level: number;
  /**
   * rng는 effect의 chance 게이트 판정용(발사 1회 단위). 미지정 시 ALWAYS_PROC(항상 발동 — 표시/QA 결정론).
   * onProc는 effect를 가진 모듈이 **발동했을 때** 호출된다(시각 연출 트리거): chance 게이트 통과 시,
   * 또는 chance 없는 모듈(= 발동률 100%, 항상 발동) 시. effect 자체가 없는 등급만 호출 안 함.
   */
  /** chanceBonus는 파이프라인 강화(정밀 모듈)가 준 발동확률 가산(0~1 clamp). chance 게이트 모듈에만 의미. */
  apply(
    spec: ProjectileSpec,
    rng?: () => number,
    onProc?: (kind: ModuleProcKind) => void,
    chanceBonus?: number,
  ): ProjectileSpec;
}

/** 모듈 등급 (지시문 §8). 골드 상자 추첨 확률 + 도감/인벤토리 등급색 + **등급별 effect 선택**에 사용. */
export type ModuleRarity = "common" | "rare" | "epic" | "legendary" | "transcendent";

/**
 * 등급 순서 (낮음→높음) — 합성 진행·획득 등급 추첨 풀 정렬·최소 등급 판정의 SSoT.
 * transcendent=최고 등급(legendary 3개 합성 전용 — 뽑기 가중치 0, drops.tsv 참조). effect/색상은 데이터(tsv)가 SSoT.
 */
export const RARITY_ORDER: readonly ModuleRarity[] = [
  "common",
  "rare",
  "epic",
  "legendary",
  "transcendent",
];

/**
 * 모듈 데이터 정의 — 메타(id/이름/설명/슬롯/등급/모양풀) + **등급별 효과**는 data/modules.tsv가 SSoT.
 * effect는 코드 하드코딩(구 *_APPLY)을 폐기하고 effect{Common,Rare,Epic,Legendary} 컬럼(mini-DSL)으로 이관했다.
 */
export interface ModuleDefinition {
  readonly id: string;
  readonly displayName: string;
  readonly description: string;
  readonly slotType: ModuleSlotType;
  readonly rarity: ModuleRarity;
  /**
   * 이 모듈의 조각(piece) 풀 — 각 조각 = (연결 모양 shapeId + 점유 칸 footprintId). 획득 시 풀에서 1개를
   * 뽑아 인스턴스에 고정한다(pickPiece). 풀에 left 포트 모양 ≥1을 보장해 Start(그리드 왼쪽 밖) 연결이
   * 항상 가능하게 한다. modules.tsv `pieces` 컬럼(`shapeId:footprintId|...`)이 SSoT. 0번 = 대표 조각(도감 미리보기).
   */
  readonly pieces: readonly ModulePiece[];
  /**
   * 등급별 효과 데이터 (modules.tsv effect{Grade} 컬럼 → parseModuleEffect). 합성으로 오른 등급은
   * effectiveRarity에 해당하는 effect를 사용한다. 시작 등급 미만 키는 없음(그 등급으로 존재하지 않음).
   */
  readonly effects: Partial<Record<ModuleRarity, ModuleEffectData>>;
}

/** 모든 모듈 공통 레벨 효과 — 레벨당 피해 배율 가산 (§17). 등급별 effect와 별개로 항상 적용. */
export function applyLevelDamage(spec: ProjectileSpec, level: number): ProjectileSpec {
  const bonus = 1 + MODULE_BRICK_BALANCE.moduleLevelDamageBonus * (level - 1);
  return { ...spec, damageMultiplier: spec.damageMultiplier * bonus };
}

/** 정의 + 레벨 + 유효 등급 → 런타임 모듈 인스턴스. */
export class DefinitionModule implements ProjectileModule {
  constructor(
    private readonly def: ModuleDefinition,
    readonly level: number,
    /** 유효 등급 — 합성 오버라이드(effectiveRarity) 반영. 미지정 시 정의 등급. 어느 effect를 쓸지 결정. */
    private readonly rarity: ModuleRarity = def.rarity,
    /**
     * 숙련도 효과 — 해금된 모듈 숙련도 노드의 effect 배열(ModuleMasteryController.masteryEffects).
     * 등급별 base effect와 별개로 **게이트 없이 항상 적용**(passive 성장축). 미지정 시 빈 배열(기존 동작).
     */
    private readonly masteryEffects: readonly ModuleEffectData[] = [],
  ) {}

  get id(): string {
    return this.def.id;
  }
  get displayName(): string {
    return this.def.displayName;
  }
  get description(): string {
    return this.def.description;
  }
  get slotType(): ModuleSlotType {
    return this.def.slotType;
  }
  apply(
    spec: ProjectileSpec,
    rng?: () => number,
    onProc?: (kind: ModuleProcKind) => void,
    chanceBonus = 0,
  ): ProjectileSpec {
    let s: ProjectileSpec = { ...spec };
    const gateRng = rng ?? ALWAYS_PROC;
    const effect = this.def.effects[this.rarity];
    if (effect) {
      const p = moduleEffectChance(effect, this.level);
      let fired = true;
      if (p !== undefined) {
        // chance 게이트 모듈 — 발동 판정 주체(rng 1회). 파이프라인 강화(정밀)가 준 chanceBonus 가산(0~1 clamp).
        const effectiveP = Math.min(1, p + Math.max(0, chanceBonus));
        fired = gateRng() < effectiveP;
      }
      if (fired) {
        // 게이트 통과 또는 chance 없는 모듈(발동률 100%) → base effect 적용 + 파이프 치명타 보너스 판정.
        s = applyModuleEffect(s, effect, this.level);
        const pipeCrit = applyPipeCriticalEffect(s, effect, this.level, rng);
        s = pipeCrit.spec;
        const procKind = pipeCrit.critical || effect.critChance || effect.critMul ? "critical" : "normal";
        onProc?.(procKind); // 전투탭 그리드 셀 표시.
      }
      // 발동 실패 시 base effect는 미적용이지만 숙련도(passive)·레벨 피해는 아래에서 그대로 적용.
    }
    // 숙련도 효과 — 게이트 없이 항상 적용(모듈 장착 + 노드 해금 시 발사마다). base 발동 여부와 무관.
    for (const me of this.masteryEffects) {
      s = applyModuleEffect(s, me, this.level, ALWAYS_PROC);
    }
    return applyLevelDamage(s, this.level);
  }
}
